import axios, { AxiosRequestConfig, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { broadcastAxiosError, normalizeAxiosError } from '../utils/apiError';
import type { UserManager } from 'oidc-client-ts';

declare global {
  interface Window {
    APP_CONFIG?: {
      API_URL?: string;
    };
  }
}

const isBrowser = typeof window !== 'undefined';

const getAPIURL = (): string => {
  if (isBrowser) {
    return process.env?.REACT_APP_API_URL || '';
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (isBrowser && window.location?.origin) {
    const origin = window.location.origin;
    if (/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(origin)) {
      return 'http://localhost:5000';
    }
    return '';
  }
  return '';
};

const API_URL = getAPIURL();

// ── Keycloak token helper ─────────────────────────────────────────────────────
// oidc-client-ts stores tokens in localStorage with key: oidc.user:<authority>:<clientId>
export const getKeycloakToken = () => {
  try {
    // Search localStorage so the session is shared across all tabs
    const oidcKey = Object.keys(localStorage).find(k => k.startsWith('oidc.user:'));
    if (!oidcKey) return null;
    
    const raw = localStorage.getItem(oidcKey);
    if (!raw) return null;
    
    const oidcData = JSON.parse(raw);
    return oidcData?.access_token || null;
  } catch (error) {
    console.error("Error retrieving Keycloak token:", error);
    return null;
  }
};

// ── OIDC UserManager registry ─────────────────────────────────────────────────
// AuthContext registers the UserManager here so the interceptor can attempt
// signinSilent() before giving up on a 401.
let _userManager: UserManager | null = null;
let _isRedirecting = false;

export const registerUserManager = (um: UserManager | null) => {
  _userManager = um;
};

/**
 * Clear all OIDC-related state from localStorage and redirect to home.
 * Prevents multiple simultaneous redirects.
 */
const clearSessionAndRedirect = () => {
  if (_isRedirecting) return;
  _isRedirecting = true;

  // Remove all OIDC keys from localStorage
  Object.keys(localStorage)
    .filter(k => k.startsWith('oidc.user:'))
    .forEach(k => localStorage.removeItem(k));

  sessionStorage.clear();
  window.location.href = '/';
};

const createClient = (overrides?: Partial<AxiosRequestConfig>) =>
  axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    timeout: 15000,
    maxRedirects: 3,
    validateStatus: (status) => status >= 200 && status < 300,
    withCredentials: true,
    ...overrides
  });

const api = createClient();

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const isAxiosError = (error: unknown): error is AxiosError => axios.isAxiosError(error);
const hasBeenBroadcast = (error: AxiosError & { __broadcasted?: boolean }) =>
  Boolean(error.__broadcasted);

// ── Request interceptor — attach Keycloak Bearer token ───────────────────────
api.interceptors.request.use(
  (config: RetryableRequestConfig) => {
    config.withCredentials = true;
    try {
      const token = getKeycloakToken();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers['Authorization'] && !config.headers['authorization']) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch (e) {
      // ignore
    }
    return config;
  },
  (requestError) => Promise.reject(requestError)
);

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error?.config ?? {}) as RetryableRequestConfig & { url?: string };
    const status = error?.response?.status;

    // 401 — token expired or invalid
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Strategy 1: Try OIDC signinSilent() if a UserManager is registered
      if (_userManager) {
        try {
          const user = await _userManager.signinSilent();
          if (user?.access_token) {
            // Token renewed — retry the original request with the new token
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${user.access_token}`;
            return api(originalRequest);
          }
        } catch (silentError) {
          console.warn('Silent token renewal failed:', silentError);
          // Fall through to strategy 2
        }
      }

      // Strategy 2: Wait briefly for automaticSilentRenew, then check localStorage
      await new Promise(resolve => setTimeout(resolve, 500));
      const newToken = getKeycloakToken();

      if (newToken) {
        // Token was silently renewed — retry the original request
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // No token after all attempts — session truly expired, redirect to login
      clearSessionAndRedirect();
      return Promise.reject(error);
    }

    // Suppress noisy errors that are expected / handled locally
    const shouldSuppress404 = status === 404;

    const isAnalyticsEndpoint = originalRequest?.url?.includes('/api/analytics');
    const shouldSuppress408 = status === 408 && isAnalyticsEndpoint;

    const isFollowEndpoint =
      originalRequest?.url?.includes('/api/users/') &&
      originalRequest?.url?.includes('/follow');
    const isInvalidUserIdError =
      status === 400 &&
      isFollowEndpoint &&
      (error?.response?.data?.message?.toLowerCase().includes('invalid user id') ||
        error?.response?.data?.message?.toLowerCase().includes('invalid user'));

    if (
      isAxiosError(error) &&
      !hasBeenBroadcast(error) &&
      !shouldSuppress404 &&
      !shouldSuppress408 &&
      !isInvalidUserIdError
    ) {
      broadcastAxiosError(error);
    }

    return Promise.reject(error);
  }
);

export { API_URL };
export default api;